import React, { ReactElement } from 'react';

import {
  UpdatingVariableProps,
  OnSaveFunction,
} from '~/scenes/Automation/Flows/Actions/Base/FlowParameter/ParameterValue/TemplateString/components/TemplateStringParameterInsertOrUpdateModal';

import { WINDOW_VARIABLE_UPDATER_DICT_NAME } from '~/components/HTMLEditor/util/variableHTML';
import cleanedFilename from '~/util/cleanedFilename';
import {
  FlowParameterMapping,
  isHandledFlowParameterMapping,
} from '../../../FlowParameterMapping/types.flow';
import ZapierMappingParameterInsertOrUpdateModal from './ZapierMappingParameterInsertOrUpdateModal';
import { FLOW_OUTPUT_TYPE } from '~/util/constants';

type ToggleVariableFunction = (variable?: UpdatingVariableProps) => void;
type ChildProps = {
  toggleVariableFunction: ToggleVariableFunction;
};
type Props = {
  variableUpdaterId: string;
  mappings: Array<FlowParameterMapping>;
  onSaveFunction: OnSaveFunction;
  children: (props: ChildProps) => React.ReactNode;
  triggerOutputType:
    | typeof FLOW_OUTPUT_TYPE.FlowOutputString
    | typeof FLOW_OUTPUT_TYPE.FlowOutputNumber
    | typeof FLOW_OUTPUT_TYPE.FlowOutputBoolean;
};
type State = {
  showVariableInsertModal: boolean;
  updatingVariable: UpdatingVariableProps | null;
};
class useZapierMappingParameterInsertOrUpdateModal extends React.Component<
  Props,
  State
> {
  constructor(props: Props) {
    super(props);

    this.state = {
      showVariableInsertModal: false,
      updatingVariable: null,
    };
  }

  componentDidMount() {
    const { variableUpdaterId, mappings } = this.props;

    if (global.window[WINDOW_VARIABLE_UPDATER_DICT_NAME] == null) {
      global.window[WINDOW_VARIABLE_UPDATER_DICT_NAME] = {};
    }

    global.window[WINDOW_VARIABLE_UPDATER_DICT_NAME][variableUpdaterId] = (
      _element,
      variableId,
    ) => {
      const variable = mappings.find(
        mapping => mapping.mappingId === variableId,
      );

      if (variable == null || variable.mapping == null)
        throw new Error(
          `${cleanedFilename(
            __filename,
          )} -> Clicked on unknown variable with id ${variableId} in ${JSON.stringify(
            variable,
            null,
            2,
          )}`,
        );

      if (!isHandledFlowParameterMapping(variable)) {
        throw Error(
          `${cleanedFilename(
            __filename,
          )} -> Cannot convert mapping from variable ${variable} since mapping type:${
            variable.mapping.type
          } is not supported`,
        );
      }
      this.showWithVariable({
        variable: variable.mapping,
        variableId,
      });
    };
  }

  closeVariableEditDropdown = () => {
    this.setState({
      showVariableInsertModal: false,
      updatingVariable: null,
    });
  };

  showWithVariable = (variable: UpdatingVariableProps | null) => {
    this.setState({
      showVariableInsertModal: true,
      updatingVariable: variable,
    });
  };

  setShowVariableInsertModal = (
    val: boolean,
    variable?: UpdatingVariableProps | null,
  ) => {
    this.setState(prevState => ({
      showVariableInsertModal: val,
      updatingVariable:
        variable !== undefined ? variable : prevState.updatingVariable,
    }));
  };

  render() {
    const { showVariableInsertModal, updatingVariable } = this.state;
    const { onSaveFunction, children, triggerOutputType } = this.props;

    let component: ReactElement | null = null;
    if (showVariableInsertModal === true) {
      component = (
        <ZapierMappingParameterInsertOrUpdateModal
          triggerOutputType={triggerOutputType}
          onSave={(newVariable, variableId) => {
            onSaveFunction(newVariable, variableId);
            this.closeVariableEditDropdown();
          }}
          onClose={() => this.setShowVariableInsertModal(false)}
          updatingVariable={updatingVariable}
        />
      );
    }

    const toggleVariableFunction = variableProps => {
      this.setShowVariableInsertModal(
        !showVariableInsertModal,
        variableProps == null ? null : variableProps,
      );
    };

    return (
      <>
        {component}
        {children({ toggleVariableFunction })}
      </>
    );
  }
}

export default useZapierMappingParameterInsertOrUpdateModal;
